import { useEffect, useState } from "react";

import { api } from "../api/client";
import { CopyButton } from "../components/CopyButton";
import { Panel } from "../components/Panel";
import { Select } from "../components/Select";
import { formatBytes, formatDateTime } from "../lib/format";
import { ui } from "../lib/ui";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";
import type { LogLevel } from "../types";

const LEVEL_TONE: Record<LogLevel, string> = {
  error: "text-danger",
  warning: "text-[#b7791f] dark:text-[#ecc94b]",
  info: "",
};

/** Settings › Logs: this PC's log files and its conversation with the hub. */
export function Logs() {
  const [tab, setTab] = useState(urlParam("tab") === "hub" ? "hub" : "files");
  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-1.5" role="tablist">
        {[
          { id: "files", label: "Log files" },
          { id: "hub", label: "Hub communication" },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`${ui.btnQuiet} ${tab === item.id ? ui.btnSelected : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <TabUrl tab={tab} setTab={setTab} />
      {tab === "files" ? <LogFilesPanel /> : <HubPanel />}
    </div>
  );
}

function TabUrl({ tab, setTab }: { tab: string; setTab: (tab: string) => void }) {
  useUrlState({ tab: tab === "hub" ? "hub" : "" }, (incoming) =>
    setTab(incoming.tab === "hub" ? "hub" : "files"),
  );
  return null;
}

function LogFilesPanel() {
  const [name, setName] = useState(urlParam("file") ?? "");
  const [level, setLevel] = useState(urlParam("level") ?? "");
  const [search, setSearch] = useState(urlParam("q") ?? "");
  const [draft, setDraft] = useState(urlParam("q") ?? "");

  useUrlState({ file: name, level, q: search }, (incoming) => {
    if ("file" in incoming) setName(incoming.file);
    if ("level" in incoming) setLevel(incoming.level);
    if ("q" in incoming) {
      setSearch(incoming.q);
      setDraft(incoming.q);
    }
  });

  const files = useAsync(() => api.logFiles(), []);
  // Nothing chosen yet: where the app's logging goes, else the file written
  // to last.
  useEffect(() => {
    if (name || !files.data?.files.length) return;
    const list = files.data.files;
    setName((list.find((file) => file.name.endsWith(".err.log")) ?? list[0]).name);
  }, [name, files.data]);

  const log = useAsync(
    () => (name ? api.logFile(name, { level, search }) : Promise.resolve(null)),
    [name, level, search],
  );

  const refresh = () => {
    files.reload();
    log.reload();
  };

  return (
    <Panel
      title="Log files"
      subtitle={files.data ? files.data.folder : undefined}
      about="Everything the service writes on this PC. Footfall.err.log holds most of it, INFO lines included: that is where the app's logging goes, so use the level filter rather than going by the file name."
      actions={
        <button type="button" className={ui.btnQuiet} onClick={refresh}>
          Refresh
        </button>
      }
    >
      {(files.error || log.error) && (
        <p className={`${ui.notice} ${ui.noticeError}`}>{files.error ?? log.error}</p>
      )}
      {files.data?.files.length === 0 && (
        <p className={ui.notice}>
          No log files yet in {files.data.folder}. The installed service writes them there; a
          development run logs to its terminal.
        </p>
      )}
      {!!files.data?.files.length && (
        <form
          className={`${ui.toolbar} mb-4`}
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(draft.trim());
          }}
        >
          <label className={ui.field}>
            File
            <Select
              wrapperClassName="min-w-80"
              value={name}
              onChange={(event) => setName(event.target.value)}
            >
              {files.data.files.map((file) => (
                <option key={file.name} value={file.name}>
                  {file.name} · {formatBytes(file.size)} · {formatDateTime(file.modified)}
                </option>
              ))}
            </Select>
          </label>
          <label className={ui.field}>
            Level
            <Select
              wrapperClassName="min-w-32"
              value={level}
              onChange={(event) => setLevel(event.target.value)}
            >
              <option value="">All</option>
              <option value="error">Errors</option>
              <option value="warning">Warnings</option>
              <option value="info">Info</option>
            </Select>
          </label>
          <label className={ui.field}>
            Search
            <span className="flex gap-2">
              <input
                className={`${ui.input} w-56`}
                type="search"
                value={draft}
                placeholder="Line contains…"
                onChange={(event) => setDraft(event.target.value)}
              />
              <button type="submit" className={ui.btnQuiet}>
                Search
              </button>
            </span>
          </label>
        </form>
      )}
      {log.data && (
        <>
          <div className="mb-2 flex items-center justify-between gap-3 text-[13px] text-muted">
            <span>
              {log.data.matched > log.data.lines.length
                ? `The last ${log.data.lines.length.toLocaleString()} of ${log.data.matched.toLocaleString()} lines`
                : `${log.data.lines.length.toLocaleString()} line${log.data.lines.length === 1 ? "" : "s"}`}
              {log.data.truncated ? " · the file's older part is not shown" : ""}
            </span>
            <CopyButton
              text={log.data.lines.map((line) => line.text).join("\n")}
              label="Copy lines"
              className={`${ui.btnQuiet} px-2! py-1! text-xs`}
            />
          </div>
          <pre className="m-0 max-h-[65vh] overflow-auto rounded-lg border border-border bg-bg p-3 font-mono text-xs leading-relaxed">
            {log.data.lines.length === 0 && (
              <span className="text-muted">Nothing here matches.</span>
            )}
            {log.data.lines.map((line, index) => (
              <div key={index} className={LEVEL_TONE[line.level]}>
                {line.text || " "}
              </div>
            ))}
          </pre>
        </>
      )}
    </Panel>
  );
}

const HUB_PAGE = 50;

function HubPanel() {
  const [failures, setFailures] = useState(urlParam("failures") === "1");
  const [page, setPage] = useState(Math.max(1, Number(urlParam("page")) || 1));
  useUrlState(
    { failures: failures ? "1" : "", page: page > 1 ? String(page) : "" },
    (incoming) => {
      if ("failures" in incoming) setFailures(incoming.failures === "1");
      if ("page" in incoming) setPage(Math.max(1, Number(incoming.page) || 1));
    },
  );
  const hub = useAsync(
    () => api.hubLog({ limit: HUB_PAGE, offset: (page - 1) * HUB_PAGE, failures }),
    [page, failures],
  );
  const data = hub.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / HUB_PAGE)) : 1;

  return (
    <>
      <Panel
        title="Link to the hub"
        about="This PC calls the hub; the hub never calls this PC. An item stays queued until the hub acknowledges it, so being offline only makes the queue longer."
        actions={
          <button type="button" className={ui.btnQuiet} onClick={hub.reload}>
            Refresh
          </button>
        }
      >
        {hub.error && <p className={`${ui.notice} ${ui.noticeError}`}>{hub.error}</p>}
        {data && (
          <>
            <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-[13px]">
              <dt className="text-muted">Hub address</dt>
              <dd className="m-0 font-mono text-xs">{data.url ?? "None: this PC is standalone"}</dd>
              <dt className="text-muted">Last registered</dt>
              <dd className="m-0">{formatDateTime(data.registered_at)}</dd>
              <dt className="text-muted">Current problem</dt>
              <dd className={`m-0 ${data.error ? "text-danger" : ""}`}>{data.error ?? "None"}</dd>
              <dt className="text-muted">Waiting to send</dt>
              <dd className="m-0">{data.pending.length}</dd>
            </dl>
            {data.pending.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className={ui.table}>
                  <thead>
                    <tr>
                      <th>What</th>
                      <th>Store</th>
                      <th>Queued</th>
                      <th>Attempts</th>
                      <th>Last error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.map((item) => (
                      <tr key={`${item.kind}:${item.branch_id}:${item.day}`}>
                        <td className="whitespace-nowrap">{`${item.kind} ${item.day}`.trim()}</td>
                        <td className="font-mono text-xs">{item.branch_id}</td>
                        <td className="whitespace-nowrap">{formatDateTime(item.queued_at)}</td>
                        <td>{item.attempts}</td>
                        <td className="break-all text-danger">{item.last_error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel
        title="Every call to the hub"
        subtitle="Newest first; the latest 5,000 are kept. Tokens and the data sent are never recorded here."
      >
        <label className="mb-3 flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={failures}
            onChange={(event) => {
              setFailures(event.target.checked);
              setPage(1);
            }}
          />
          Only failures
        </label>
        {data && (
          <>
            <div className="overflow-x-auto">
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Result</th>
                    <th>Action</th>
                    <th title="agent = the background sync; console = a button in this console">
                      From
                    </th>
                    <th>Store</th>
                    <th>Took</th>
                    <th>Sent / received</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((event) => (
                    <tr key={event.id}>
                      <td className="whitespace-nowrap">
                        {formatDateTime(event.at)}
                        {event.repeats > 1 && (
                          <div className="text-xs text-muted">
                            {event.repeats.toLocaleString()} times since{" "}
                            {formatDateTime(event.first_at)}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`${ui.pill} ${event.ok ? ui.pillSucceeded : ui.pillFailed}`}>
                          {event.status ?? (event.ok ? "ok" : "no answer")}
                        </span>
                      </td>
                      <td className="whitespace-nowrap" title={event.url ?? undefined}>
                        {event.action}
                      </td>
                      <td>{event.source}</td>
                      <td className="font-mono text-xs">{event.branch_id ?? "—"}</td>
                      <td className="whitespace-nowrap">
                        {event.duration_ms === null ? "—" : `${event.duration_ms} ms`}
                      </td>
                      <td className="whitespace-nowrap">
                        {event.method
                          ? `${formatBytes(event.sent_bytes ?? 0)} / ${formatBytes(event.received_bytes ?? 0)}`
                          : "—"}
                      </td>
                      <td className={`break-all ${event.ok ? "text-muted" : "text-danger"}`}>
                        {event.url && !event.ok && (
                          <div className="font-mono text-xs">
                            {event.method} {event.url}
                          </div>
                        )}
                        {event.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.events.length === 0 && (
              <p className="py-6 text-center text-muted">
                {failures ? "No failed calls recorded." : "No calls recorded yet."}
              </p>
            )}
            <div className="mt-3 flex items-center justify-end gap-2 text-[13px] text-muted">
              <button
                type="button"
                className={ui.btnQuiet}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              Page {page} of {pages}
              <button
                type="button"
                className={ui.btnQuiet}
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}
